import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Mode = "light" | "dark";

interface ThemeValue {
  mode: Mode;
  setMode: (m: Mode) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

const MODE_KEY = "flashadz-partner-mode";

function initialMode(): Mode {
  try {
    const stored = localStorage.getItem(MODE_KEY);
    if (stored === "light" || stored === "dark") return stored;
    return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark";
  } catch {
    return "dark";
  }
}

/**
 * The portal has one brand accent (teal). Only the light/dark mode changes.
 * U0: the portal accent is built teal to keep parity with the owner app. "Teal
 * parity vs re-token to violet for the portal" is already an open question in
 * GRAPH-v2 section 9; not re-adding it here, building teal per that decision. If
 * it flips to violet later, only the token values in styles/index.css change.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<Mode>(initialMode);

  useEffect(() => {
    document.documentElement.dataset.mode = mode;
  }, [mode]);

  const setMode = useCallback((m: Mode) => {
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      // Storage can be unavailable (private mode); the in-memory state still applies.
    }
    setModeState(m);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((m) => {
      const next: Mode = m === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(MODE_KEY, next);
      } catch {
        // Ignore storage failures; the toggle still updates the live theme.
      }
      return next;
    });
  }, []);

  const value = useMemo<ThemeValue>(
    () => ({ mode, setMode, toggleMode }),
    [mode, setMode, toggleMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
